import Phaser from 'phaser'
import i18n from '../../i18n.js'

export interface PackOpenSceneData {
  cards: Array<{ cardId: string; rarity: string; isNew: boolean }>
  packType: string
}

const RARITY_COLORS: Record<string, number> = {
  common: 0x444444,
  rare: 0x1a3a8a,
  epic: 0x4a1a8a,
  legendary: 0xc9a84c,
}

const RARITY_LABEL_COLORS: Record<string, string> = {
  common: '#aaaaaa',
  rare: '#4a90e2',
  epic: '#a855f7',
  legendary: '#c9a84c',
}

const CARD_W = 80
const CARD_H = 120
const CARD_SPACING = 100
const FLIP_HALF_MS = 200

export class PackOpenScene extends Phaser.Scene {
  private sceneData!: PackOpenSceneData
  private cardContainers: Phaser.GameObjects.Container[] = []
  private revealedCount = 0
  private allRevealed = false
  private closeButton?: Phaser.GameObjects.Text
  private revealAllButton?: Phaser.GameObjects.Text
  private tapHintText?: Phaser.GameObjects.Text
  private isAnimating = false
  private _i18nCleanup: (() => void) | null = null

  constructor() {
    super({ key: 'PackOpenScene' })
  }

  init(data: PackOpenSceneData): void {
    this.sceneData = data
    this.cardContainers = []
    this.revealedCount = 0
    this.allRevealed = false
    this.isAnimating = false
  }

  create(): void {
    const { width, height } = this.scale

    // Dark background
    this.add.rectangle(width / 2, height / 2, width, height, 0x0a0810)

    // Title
    this.add
      .text(width / 2, 48, this.sceneData.packType, {
        fontSize: '28px',
        fontFamily: 'sans-serif',
        color: '#c9a84c',
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0.5)

    // Subtitle
    this.tapHintText = this.add
      .text(width / 2, 84, i18n.t('PHASER_PACK_TAP_HINT'), {
        fontSize: '14px',
        fontFamily: 'sans-serif',
        color: '#a090c0',
      })
      .setOrigin(0.5, 0.5)

    // Build card row
    const cards = this.sceneData.cards
    const totalW = cards.length * CARD_SPACING - (CARD_SPACING - CARD_W)
    const startX = width / 2 - totalW / 2 + CARD_W / 2
    const cardY = height / 2

    cards.forEach((cardData, idx) => {
      const x = startX + idx * CARD_SPACING
      const container = this.add.container(x, cardY)
      container.setSize(CARD_W, CARD_H)

      // Card back
      const back = this.add.rectangle(0, 0, CARD_W, CARD_H, 0x1a0f2e)
      back.setStrokeStyle(2, 0x6d28d9)

      // Diamond pattern on back
      const diamondGfx = this.add.graphics()
      diamondGfx.lineStyle(1, 0x4a1a8a, 0.8)
      for (let row = -2; row <= 2; row++) {
        for (let col = -1; col <= 1; col++) {
          const dx = col * 28 + (row % 2 === 0 ? 0 : 14)
          const dy = row * 20
          diamondGfx.beginPath()
          diamondGfx.moveTo(dx, dy - 10)
          diamondGfx.lineTo(dx + 12, dy)
          diamondGfx.lineTo(dx, dy + 10)
          diamondGfx.lineTo(dx - 12, dy)
          diamondGfx.closePath()
          diamondGfx.strokePath()
        }
      }

      // Card lock icon (back face)
      const lockText = this.add
        .text(0, 0, '?', {
          fontSize: '32px',
          fontFamily: 'sans-serif',
          color: '#6d28d9',
          fontStyle: 'bold',
        })
        .setOrigin(0.5, 0.5)

      container.add([back, diamondGfx, lockText])

      // Store card data on container for reveal
      ;(container as Phaser.GameObjects.Container & { __cardData: typeof cardData }).__cardData =
        cardData
      ;(
        container as Phaser.GameObjects.Container & {
          __backElements: Phaser.GameObjects.GameObject[]
        }
      ).__backElements = [back, diamondGfx, lockText]

      // Interactive
      container.setInteractive()
      container.on('pointerdown', () => this.revealCard(idx))
      container.on('pointerover', () => {
        if (this.revealedCount <= idx && !this.allRevealed) {
          back.setStrokeStyle(3, 0xa855f7)
        }
      })
      container.on('pointerout', () => {
        back.setStrokeStyle(2, 0x6d28d9)
      })

      this.cardContainers.push(container)
    })

    // "Reveal all" button
    this.revealAllButton = this.add
      .text(width / 2, height - 100, i18n.t('PHASER_PACK_REVEAL_ALL'), {
        fontSize: '18px',
        fontFamily: 'sans-serif',
        color: '#ffffff',
        backgroundColor: '#4a1a8a',
        padding: { x: 20, y: 12 },
      })
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true })

    this.revealAllButton.on('pointerdown', () => this.revealAll())
    this.revealAllButton.on('pointerover', () =>
      this.revealAllButton?.setStyle({ backgroundColor: '#6d28d9' })
    )
    this.revealAllButton.on('pointerout', () =>
      this.revealAllButton?.setStyle({ backgroundColor: '#4a1a8a' })
    )

    // "Close" button — hidden until all revealed
    this.closeButton = this.add
      .text(width / 2, height - 50, i18n.t('PHASER_PACK_CLOSE'), {
        fontSize: '18px',
        fontFamily: 'sans-serif',
        color: '#ffffff',
        backgroundColor: '#1a3a8a',
        padding: { x: 24, y: 12 },
      })
      .setOrigin(0.5, 0.5)
      .setVisible(false)
      .setInteractive({ useHandCursor: true })

    this.closeButton.on('pointerdown', () => this.closeScene())
    this.closeButton.on('pointerover', () =>
      this.closeButton?.setStyle({ backgroundColor: '#2a5ab0' })
    )
    this.closeButton.on('pointerout', () =>
      this.closeButton?.setStyle({ backgroundColor: '#1a3a8a' })
    )

    // React to language changes while this scene is active
    const onLangChanged = () => this._refreshI18nLabels()
    i18n.on('languageChanged', onLangChanged)
    this._i18nCleanup = () => i18n.off('languageChanged', onLangChanged)
  }

  private _refreshI18nLabels(): void {
    this.tapHintText?.setText(i18n.t('PHASER_PACK_TAP_HINT'))
    this.revealAllButton?.setText(i18n.t('PHASER_PACK_REVEAL_ALL'))
    this.closeButton?.setText(i18n.t('PHASER_PACK_CLOSE'))
  }

  shutdown(): void {
    this._i18nCleanup?.()
    this._i18nCleanup = null
  }

  private revealCard(idx: number): void {
    if (idx !== this.revealedCount || this.isAnimating) return
    this.doFlip(idx, () => {
      this.revealedCount++
      if (this.revealedCount >= this.cardContainers.length) {
        this.showCloseButton()
      }
    })
  }

  private revealAll(): void {
    if (this.allRevealed || this.isAnimating) return
    this.allRevealed = true
    this.revealAllButton?.setVisible(false)

    const revealNext = (idx: number) => {
      if (idx >= this.cardContainers.length) {
        this.revealedCount = this.cardContainers.length
        this.showCloseButton()
        return
      }
      // Skip already-revealed cards
      const container = this.cardContainers[idx]
      const alreadyRevealed = (
        container as Phaser.GameObjects.Container & { __revealed?: boolean }
      ).__revealed
      if (alreadyRevealed) {
        revealNext(idx + 1)
        return
      }
      this.doFlip(idx, () => {
        this.time.delayedCall(80, () => revealNext(idx + 1))
      })
    }

    revealNext(0)
  }

  private doFlip(
    idx: number,
    onComplete: () => void
  ): void {
    const container = this.cardContainers[idx] as Phaser.GameObjects.Container & {
      __cardData: PackOpenSceneData['cards'][number]
      __backElements: Phaser.GameObjects.GameObject[]
      __revealed?: boolean
    }
    if (container.__revealed) return
    container.__revealed = true
    this.isAnimating = true

    // Half 1: scale X to 0
    this.tweens.add({
      targets: container,
      scaleX: 0,
      duration: FLIP_HALF_MS,
      ease: 'Sine.easeIn',
      onComplete: () => {
        // Swap contents to front face
        container.__backElements.forEach((obj) => {
          (obj as Phaser.GameObjects.GameObject & { setVisible: (v: boolean) => void }).setVisible(
            false
          )
        })
        this.buildCardFront(container, container.__cardData)

        // Half 2: scale X back to 1
        this.tweens.add({
          targets: container,
          scaleX: 1,
          duration: FLIP_HALF_MS,
          ease: 'Sine.easeOut',
          onComplete: () => {
            this.isAnimating = false
            onComplete()
          },
        })
      },
    })
  }

  private buildCardFront(
    container: Phaser.GameObjects.Container,
    cardData: PackOpenSceneData['cards'][number]
  ): void {
    const rarity = cardData.rarity in RARITY_COLORS ? cardData.rarity : 'common'
    const bgColor = RARITY_COLORS[rarity] ?? 0x444444
    const labelColor = RARITY_LABEL_COLORS[rarity] ?? '#aaaaaa'

    const bg = this.add.rectangle(0, 0, CARD_W, CARD_H, bgColor)
    bg.setStrokeStyle(2, 0xffffff, 0.4)

    // Rarity banner at top
    const rarityBg = this.add.rectangle(0, -CARD_H / 2 + 12, CARD_W, 20, 0x000000, 0.5)

    const rarityText = this.add
      .text(0, -CARD_H / 2 + 12, rarity.toUpperCase(), {
        fontSize: '10px',
        fontFamily: 'sans-serif',
        color: labelColor,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0.5)

    // Card id label (shortened)
    const shortId = cardData.cardId.length > 10 ? cardData.cardId.slice(0, 10) + '…' : cardData.cardId
    const nameText = this.add
      .text(0, 10, shortId, {
        fontSize: '11px',
        fontFamily: 'sans-serif',
        color: '#f0e6ff',
        wordWrap: { width: CARD_W - 8 },
        align: 'center',
      })
      .setOrigin(0.5, 0.5)

    // Decorative element icon (rarity initial as large letter)
    const iconLetter = rarity[0]?.toUpperCase() ?? '?'
    const iconText = this.add
      .text(0, -10, iconLetter, {
        fontSize: '28px',
        fontFamily: 'sans-serif',
        color: labelColor,
        fontStyle: 'bold',
      })
      .setAlpha(0.5)
      .setOrigin(0.5, 0.5)

    container.add([bg, rarityBg, rarityText, iconText, nameText])

    // "NEW!" badge if isNew
    if (cardData.isNew) {
      const newBg = this.add.rectangle(CARD_W / 2 - 18, -CARD_H / 2 + 28, 32, 16, 0xc9a84c)
      const newText = this.add
        .text(CARD_W / 2 - 18, -CARD_H / 2 + 28, i18n.t('PHASER_PACK_NEW_BADGE'), {
          fontSize: '9px',
          fontFamily: 'sans-serif',
          color: '#000000',
          fontStyle: 'bold',
        })
        .setOrigin(0.5, 0.5)
      container.add([newBg, newText])
    }
  }

  private showCloseButton(): void {
    this.revealAllButton?.setVisible(false)
    this.closeButton?.setVisible(true)
  }

  private closeScene(): void {
    // Emit event so React layer can react
    window.dispatchEvent(new CustomEvent('ultimaze:pack-scene-closed'))
    this.scene.stop()
  }
}
